import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowDown, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import { api, imageUrl } from "@/lib/api";
import {
  STUDIO,
  HERO_IMAGE,
  WORKSHOP_IMAGE,
  SPOTLIGHT_IMAGE,
  SERVICES,
  MANIFESTO,
} from "@/lib/content";

import GALLERY from '@/lib/creations';

const Home = () => {
  const heroRef = useRef(null);
  const teaser = GALLERY.slice(0, 4);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  // États pour la modale (Lightbox)
  const [selectedCreation, setSelectedCreation] = useState(null);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  const openModal = (creation) => {
    setSelectedCreation(creation);
    setCurrentImgIndex(0);
  };

  const closeModal = () => {
    setSelectedCreation(null);
  };

  const nextImage = (e) => {
    if (e) e.stopPropagation();
    if (!selectedCreation) return;
    const images = selectedCreation.images || [selectedCreation.src];
    setCurrentImgIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    if (e) e.stopPropagation();
    if (!selectedCreation) return;
    const images = selectedCreation.images || [selectedCreation.src];
    setCurrentImgIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[100vh] flex flex-col justify-end overflow-hidden pt-32 pb-16 px-6 md:px-12"
      >
        <motion.div
          className="absolute inset-0 z-0"
          style={{ y: imgY, scale: imgScale }}
        >
          <img
            src={HERO_IMAGE}
            alt="Intérieur bois façonné"
            className="w-full h-[125%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F4F1EB] via-[#F4F1EB]/20 to-[#1C1A18]/30" />
        </motion.div>

        <div className="relative z-10 max-w-[1600px] mx-auto w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="text-xs uppercase tracking-[0.25em] text-white font-semibold mb-6 md:mb-10"
          >
            Ébénisterie — {STUDIO.city}
          </motion.p>
          <h1 className="font-hero-sq font-semibold text-[12vw] md:text-[8.5vw] leading-[0.95] tracking-tight text-[#1C1A18]">
            <MaskLine delay={0.1}>Le bois,</MaskLine>
            <MaskLine delay={0.25}>
              <span className="text-[#8B5A33]">façonné</span>
            </MaskLine>
            <MaskLine delay={0.4}>
              à la main
            </MaskLine>
          </h1>

          <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end justify-between gap-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.9 }}
              className="max-w-md text-lg font-light text-[#3A362F]"
            >
              Meubles sur mesure et agencement d'intérieur, conçus et fabriqués
              dans le respect de la matière et du geste artisanal.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 1 }}
            >
              <Link
                to="/galerie"
                data-testid="hero-gallery-cta"
                className="group inline-flex items-center gap-3 bg-[#1C1A18] text-[#F4F1EB] rounded-full pl-8 pr-6 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#8B5A33] transition-colors duration-500"
              >
                Voir les créations
                <ArrowUpRight
                  size={18}
                  className="group-hover:rotate-45 transition-transform duration-500"
                />
              </Link>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="relative z-10 max-w-[1600px] mx-auto w-full mt-12 flex items-center gap-2 text-[#5C564D]"
        >
          <ArrowDown size={16} className="animate-bounce" />
          <span className="text-xs uppercase tracking-[0.2em]">Défiler</span>
        </motion.div>
      </section>

      {/* INTRO / SERVICES */}
      <section className="px-6 md:px-12 py-28 md:py-40">
        <div className="max-w-[1600px] mx-auto">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-[#8B5A33] mb-8">
              Ce que je propose
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-el font-light text-4xl md:text-6xl leading-[1.05] max-w-4xl tracking-tight">
              Des pièces uniques, pensées pour durer et pour habiter vos
              espaces avec caractère.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-px mt-20 md:mt-28 bg-[#1C1A18]/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div
                  data-testid={`service-${s.num}`}
                  className="bg-[#F4F1EB] p-8 md:p-10 h-full hover:bg-[#EAE5D9] transition-colors duration-500"
                >
                  <h3 className="font-serif-el text-3xl mb-4">{s.title}</h3>
                  <p className="font-light text-[#5C564D] leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO — dark */}
      <section className="bg-[#1C1A18] text-[#F4F1EB] px-6 md:px-12 py-28 md:py-40">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <Reveal>
            <div className="relative max-w-[400px]">
              <div className="overflow-hidden rounded-sm">
                <img
                  src={WORKSHOP_IMAGE}
                  alt="En atelier"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-4 md:-right-6 bg-[#8B5A33] text-[#F4F1EB] px-6 py-4 text-xs uppercase tracking-[0.2em]">
                Fait main
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C49A76] mb-10">
                Manifeste
              </p>
            </Reveal>
            <div className="divide-y divide-[#F4F1EB]/10">
              {MANIFESTO.map((m, i) => (
                <Reveal key={m.num} delay={i * 0.1}>
                  <div className="py-8 flex gap-6 md:gap-10">
                    <div>
                      <h3 className="font-serif-el text-3xl md:text-4xl mb-3">{m.title}</h3>
                      <p className="font-light text-[#F4F1EB]/70 leading-relaxed max-w-md">
                        {m.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SPOTLIGHT */}
      <section className="px-6 md:px-12 py-28 md:py-40">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <div className="overflow-hidden rounded-sm max-w-[440px] mx-auto">
              <motion.img
                src={SPOTLIGHT_IMAGE}
                alt="Détail de fabrication"
                className="w-full h-auto object-contain"
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </Reveal>
          <div className="lg:col-span-5 lg:pl-8">
            <Reveal delay={0.1}>
              <p className="text-xs uppercase tracking-[0.2em] text-[#8B5A33] mb-6">
                L'atelier
              </p>
              <h2 className="font-serif-el font-light text-4xl md:text-5xl leading-tight mb-6">
                Chaque détail compte
              </h2>
              <p className="font-light text-[#5C564D] leading-relaxed mb-8">
                Du choix de l'essence à la dernière couche d'huile, la
                fabrication est menée entièrement à l'atelier. Assemblages
                traditionnels, finitions naturelles et exigence à chaque étape.
              </p>
              <Link
                to="/contact"
                data-testid="spotlight-contact-cta"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] border-b border-[#1C1A18] pb-1 hover:text-[#8B5A33] hover:border-[#8B5A33] transition-colors duration-300"
              >
                Me contacter <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GALLERY TEASER */}
      <section className="px-6 md:px-12 pb-28 md:pb-40">
        <div className="max-w-[1600px] mx-auto">
          <Reveal>
            <div className="flex items-end justify-between mb-12 border-b border-[#1C1A18]/10 pb-8">
              <h2 className="font-serif-el font-light text-4xl md:text-6xl">
                Créations récentes
              </h2>
              <Link
                to="/galerie"
                data-testid="teaser-gallery-link"
                className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] hover:text-[#8B5A33] transition-colors duration-300"
              >
                Toute la galerie <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
          
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {teaser.map((g, i) => {
              const isVideo = /\.(mp4|mov|webm)$/i.test(g.src);

              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div 
                    onClick={() => openModal(g)}
                    data-testid={`teaser-image-${i}`} 
                    className="group block cursor-pointer"
                  >
                    <div className="overflow-hidden rounded-sm">
                      {isVideo ? (
                        <video
                          src={g.src}
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]"
                        />
                      ) : (
                        <img
                          src={g.src}
                          alt={g.title}
                          className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]"
                        />
                      )}
                    </div>
                    <div className="mt-4 flex flex-col gap-1 border-b border-[#1C1A18]/10 pb-3">
                      <span className="font-sans text-2xl">{g.title}</span>
                      <span className="text-xs text-[#8B5A33]">
                        {g.tag}
                      </span>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MODALE LIGHTBOX */}
      <AnimatePresence>
        {selectedCreation && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-[#1C1A18]/95 backdrop-blur-sm p-4 md:p-8"
            onClick={() => {
              setSelectedCreation(null);
              setTimeout(() => setCurrentImgIndex(0), 300);
            }}
          >
            <div 
              className="relative w-full max-w-6xl h-full flex flex-col justify-center" 
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                data-testid="lightbox-close"
                onClick={() => setSelectedCreation(null)} 
                className="absolute -top-2 right-0 md:top-0 md:-right-8 text-[#F4F1EB] hover:text-[#8B5A33] transition-colors z-[60]"
              >
                <X size={36} strokeWidth={1.5} />
              </button>

              {/* Conteneur Média */}
              <div className="relative flex-1 min-h-0 w-full flex items-center justify-center mt-6 md:mt-0">
                
                {selectedCreation.images && selectedCreation.images.length > 1 && (
                  <>
                    <button 
                      onClick={prevImage} 
                      className="absolute left-0 md:-left-12 top-1/2 -translate-y-1/2 text-[#F4F1EB] hover:text-[#8B5A33] transition-colors z-50 cursor-pointer hidden md:block"
                    >
                      <ChevronLeft size={48} strokeWidth={1} />
                    </button>
                    <button 
                      onClick={nextImage} 
                      className="absolute right-0 md:-right-12 top-1/2 -translate-y-1/2 text-[#F4F1EB] hover:text-[#8B5A33] transition-colors z-50 cursor-pointer hidden md:block"
                    >
                      <ChevronRight size={48} strokeWidth={1} />
                    </button>
                  </>
                )}

                {(() => {
                  const mediaList = selectedCreation.images || [selectedCreation.src];
                  const currentMediaSrc = mediaList[currentImgIndex];
                  const canSwipe = mediaList.length > 1;

                  const isVideo = typeof currentMediaSrc === 'string' && (
                    currentMediaSrc.toLowerCase().includes('.mp4') || 
                    currentMediaSrc.toLowerCase().includes('.mov') || 
                    currentMediaSrc.toLowerCase().includes('.webm')
                  );
                  
                  return (
                    <motion.div
                      drag={canSwipe ? "x" : false}
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.8}
                      onDragEnd={(e, { offset }) => {
                        if (!canSwipe) return;
                        const swipeThreshold = 50;
                        if (offset.x < -swipeThreshold) {
                          nextImage(e);
                        } else if (offset.x > swipeThreshold) {
                          prevImage(e);
                        }
                      }}
                      className={`w-full h-full flex items-center justify-center ${
                        canSwipe ? 'cursor-grab active:cursor-grabbing touch-pan-y' : ''
                      }`}
                    >
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={currentMediaSrc}
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.98 }}
                          transition={{ duration: 0.2 }}
                          className="w-full h-full flex items-center justify-center pointer-events-none"
                        >
                          {isVideo ? (
                            <video 
                              src={currentMediaSrc} 
                              controls 
                              autoPlay 
                              playsInline
                              onPointerDownCapture={(e) => {
                                const rect = e.target.getBoundingClientRect();
                                const clickY = e.clientY - rect.top;
                                if (rect.height - clickY < 70) {
                                  e.stopPropagation();
                                }
                              }}
                              className="max-w-full max-h-full object-contain shadow-2xl relative z-10 pointer-events-auto" 
                            />
                          ) : (
                            <img 
                              src={currentMediaSrc} 
                              alt={selectedCreation.title} 
                              className="max-w-full max-h-full object-contain shadow-2xl select-none pointer-events-auto" 
                            />
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </motion.div>
                  );
                })()}
              </div>

              {/* Conteneur Texte et Miniatures */}
              <div className="w-full shrink-0 pt-4 md:pt-6 pb-2 text-[#F4F1EB] z-10 overflow-y-auto max-h-[40vh] md:max-h-[30vh]">
                
                {/* Ligne des Miniatures */}
                {selectedCreation.images && selectedCreation.images.length > 1 && (
                  <div 
                    className="flex items-center gap-2 md:gap-3 mb-4 overflow-x-auto pb-2 scroll-smooth"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} // Cache la scrollbar native
                  >
                    {selectedCreation.images.map((src, idx) => {
                      const isThumbVideo = typeof src === 'string' && (
                        src.toLowerCase().includes('.mp4') || 
                        src.toLowerCase().includes('.mov') || 
                        src.toLowerCase().includes('.webm')
                      );
                      const isActive = currentImgIndex === idx;

                      return (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImgIndex(idx);
                          }}
                          className={`relative shrink-0 overflow-hidden rounded-sm transition-all duration-300 ${
                            isActive 
                              ? "w-12 h-12 md:w-16 md:h-16 border-2 border-[#8B5A33] opacity-100 scale-105" 
                              : "w-10 h-10 md:w-14 md:h-14 border border-[#F4F1EB]/20 opacity-50 hover:opacity-100"
                          }`}
                        >
                          {isThumbVideo ? (
                            <video 
                              src={src} 
                              className="w-full h-full object-cover pointer-events-none" 
                            />
                          ) : (
                            <img 
                              src={src} 
                              alt={`Vue ${idx + 1}`} 
                              className="w-full h-full object-cover pointer-events-none" 
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Titre et Tags */}
                <h3 className="font-serif-el text-2xl md:text-3xl drop-shadow-lg">
                  {selectedCreation.title}
                </h3>
                
                {selectedCreation.tag && (
                  <p className="text-xs md:text-sm mt-2 text-[#C49A76] tracking-normal drop-shadow-md whitespace-pre-line">
                    {selectedCreation.tag}
                  </p>
                )}

                {/* Compteur conservé discrètement en plus des miniatures */}
                {selectedCreation.images && selectedCreation.images.length > 1 && (
                  <p className="text-xs font-light tracking-normal mt-3 opacity-70">
                    {currentImgIndex + 1} / {selectedCreation.images.length}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home;